import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Tabs,
  Tab,
  Collapse,
  Alert
} from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import DescriptionIcon from '@mui/icons-material/Description';
import TemplateIcon from '@mui/icons-material/AutoAwesomeMotion';
import CollectionsIcon from '@mui/icons-material/Collections';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettings/GlobalSettingUtils';
import { useWebSocket } from './WebSocketContext';
import { ModelSettings } from './GlobalSettings/ModelSettings';
import { PerformanceSettings } from './GlobalSettings/PerformanceSettingsNew';
import { GlobalSettingFiles } from './GlobalSettings/GlobalSettingFilesNew';
import { TemplateSettings } from './GlobalSettings/TemplateSettings';
import CollectionSettings from './GlobalSettings/CollectionSettings';
import GlobalSettingLogs from './GlobalSettings/GlobalSettingLogs';
import GlobalSettingActionsHistory from './GlobalSettings/GlobalSettingActionsHistory';
import AccountSettings from './GlobalSettings/AccountSettings';
import ModelManagement from './GlobalSettings/ModelManagement';

// TabPanel helper with improved styling and mobile support
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  const visible = value === index;
  return (
    <div role="tabpanel" aria-hidden={!visible} {...other}>
      <Collapse in={visible} timeout={300} unmountOnExit>
        <Box sx={{
          p: { xs: 1.5, sm: 2.5, md: 3 },
          background: 'white',
          borderRadius: { xs: '0 0 6px 6px', sm: '0 0 8px 8px' },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(87, 87, 176, 0.2) 50%, transparent 100%)',
          },
          // Use transform for subtle reveal but do not force inner scrollbars
          transition: 'opacity 280ms ease, transform 300ms cubic-bezier(0.19, 1, 0.22, 1)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0px)' : 'translateY(6px)'
        }}>
          {children}
        </Box>
      </Collapse>
    </div>
  );
}

const GlobalSetting = () => {
  const { 
    credit,
    t,
    ws
  } = useGlobalSettingState();
  const { storageInfo, mfaSetupRequired } = useWebSocket();
  const isRestrictToUserFolder = Boolean(storageInfo?.restrictToUserFolder);

  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState({
    modelTextHost: 'openai',
    modelTextHostId: '',
    modelVisionHost: 'openai',
    modelVisionHostId: '',
    modelAudioHost: 'openai',
    modelAudioHostId: '',
    modelWidgetHost: '',
    modelWidgetHostId: '',
    modelProjectHost: '',
    modelProjectHostId: ''
  });
  const [allowedProviders, setAllowedProviders] = useState([]);
  const [savedModels, setSavedModels] = useState({
    text: [],
    vision: [],
    audio: []
  });

  // Define consistent colors and styling
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';
  const hoverColor = '#aaaaca';

  // Callback to update parent config state from child components
  const updateParentConfig = (newConfig) => {
    setConfig(prev => ({
      ...prev,
      ...newConfig
    }));
  };

  const normalizeAllowedProviders = useCallback((providers) => {
    if (!providers || !Array.isArray(providers)) return [];
    return providers.map((p) => (p ? String(p).toLowerCase() : '')).filter(Boolean);
  }, []);

  const filterSavedModels = useCallback((models, allowed = allowedProviders) => {
    const allowedSet = new Set((allowed || []).map((p) => p.toLowerCase()).filter(Boolean));
    const baseModels = models || {};
    const filterList = (list = []) => {
      if (!allowedSet.size) return list;
      return list.filter((model) => allowedSet.has((model.provider || '').toLowerCase()));
    };
    return {
      text: filterList(baseModels.text || []),
      vision: filterList(baseModels.vision || []),
      audio: filterList(baseModels.audio || [])
    };
  }, [allowedProviders]);

  // References to child components to access their state
  const modelSettingsRef = useRef(null);
  const performanceSettingsRef = useRef(null);
  const formatSettingsRef = useRef(null);
  const templatesSettingsRef = useRef(null);
  const collectionsSettingsRef = useRef(null);
  const logsSettingsRef = useRef(null);
  const actionsHistorySettingsRef = useRef(null);
  const accountSettingsRef = useRef(null);

  // Define handlers for WebSocket messages
  const commandHandlers = {
    'update_configuration': (message) => {
      if (message.data === 'success') {
        // Each component will handle its own success message
        // Request updated configuration to refresh the display
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ command: 'configuration_check' }));
        }
      }
    },
    'configuration_data': (message) => {
      const allowed = normalizeAllowedProviders(message.data.allowedModelProviders || []);
      setAllowedProviders(allowed);
      setConfig({
        modelTextHost: message.data.modelTextHost || 'openai',
        modelTextHostId: message.data.modelTextHostId ? String(message.data.modelTextHostId) : '',
        modelVisionHost: message.data.modelVisionHost || 'openai',
        modelVisionHostId: message.data.modelVisionHostId ? String(message.data.modelVisionHostId) : '',
        modelAudioHost: message.data.model_audio_host || message.data.modelAudioHost || 'openai',
        modelAudioHostId: message.data.modelAudioHostId ? String(message.data.modelAudioHostId) : '',
        modelWidgetHost: message.data.modelWidgetHost || '',
        modelWidgetHostId: message.data.modelWidgetHostId ? String(message.data.modelWidgetHostId) : '',
        modelProjectHost: message.data.modelProjectHost || '',
        modelProjectHostId: message.data.modelProjectHostId ? String(message.data.modelProjectHostId) : ''
      });
    },
    'configuration_check': () => {
      // Configuration check handling if needed
    },
    'saved_models': (message) => {
      const allowed = normalizeAllowedProviders(message.data?.allowedModelProviders || allowedProviders);
      if (allowed.length) {
        setAllowedProviders(allowed);
      }
      setSavedModels(filterSavedModels(message.data || { text: [], vision: [], audio: [] }, allowed.length ? allowed : allowedProviders));
    }
  };
  
  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    setSavedModels((prev) => filterSavedModels(prev));
  }, [filterSavedModels, allowedProviders]);

  useEffect(() => {
    // Request configuration data when component mounts
    sendCommand('configuration_check');
    sendCommand('get_saved_models');
  }, [sendCommand]);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        key: 'model-management',
        icon: <ManageAccountsIcon />,
        label: t('global_setting.model_management_section', 'Model Management'),
        panel: (
          <ModelManagement
            savedModels={savedModels}
            allowedModelProviders={allowedProviders}
            onModelsUpdate={() => sendCommand('get_saved_models')}
          />
        )
      },
      {
        key: 'model-settings',
        icon: <SettingsIcon />,
        label: t('global_setting.model_settings_section', 'Model Settings'),
        panel: <ModelSettings ref={modelSettingsRef} onConfigChange={updateParentConfig} savedModels={savedModels} />
      },
      {
        key: 'performance',
        icon: <SpeedIcon />,
        label: t('global_setting.performance_section'),
        panel: <PerformanceSettings ref={performanceSettingsRef} />
      },
      {
        key: 'format',
        icon: <DescriptionIcon />,
        label: t('global_setting.format_section'),
        panel: <GlobalSettingFiles ref={formatSettingsRef} />
      },
      {
        key: 'templates',
        icon: <TemplateIcon />,
        label: t('global_setting.templates_section', 'Templates'),
        panel: <TemplateSettings ref={templatesSettingsRef} />
      },
      {
        key: 'collections',
        icon: <CollectionsIcon />,
        label: t('global_setting.collections_section', 'Collections'),
        panel: <CollectionSettings ref={collectionsSettingsRef} />
      }
    ];

    if (!isRestrictToUserFolder) {
      baseTabs.push({
        key: 'logs',
        icon: <ArticleIcon />,
        label: t('global_setting.logs_section', 'Logs'),
        panel: <GlobalSettingLogs ref={logsSettingsRef} />
      });
    }

    baseTabs.push(
      {
        key: 'actions-history',
        icon: <HistoryIcon />,
        label: t('global_setting.actions_history_section', 'Actions History'),
        panel: <GlobalSettingActionsHistory ref={actionsHistorySettingsRef} />
      },
      {
        key: 'account',
        icon: <AccountCircleIcon />,
        label: t('global_setting.account_section', 'Account'),
        panel: <AccountSettings ref={accountSettingsRef} />
      }
    );

    return baseTabs;
  }, [
    isRestrictToUserFolder,
    t,
    savedModels,
    allowedProviders,
    sendCommand,
    updateParentConfig
  ]);

  const accountTabIndex = useMemo(() => {
    return tabs.findIndex((tab) => tab.key === 'account');
  }, [tabs]);

  const handleTabChange = (event, newValue) => {
    if (mfaSetupRequired && newValue !== accountTabIndex) {
      return;
    }
    setTabValue(newValue);
  };

  useEffect(() => {
    if (tabValue >= tabs.length) {
      setTabValue(0);
    }
  }, [tabValue, tabs.length]);

  useEffect(() => {
    if (mfaSetupRequired && accountTabIndex >= 0 && tabValue !== accountTabIndex) {
      setTabValue(accountTabIndex);
    }
  }, [mfaSetupRequired, accountTabIndex, tabValue]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: {
          xs: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          sm: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        },
        py: { xs: 2, sm: 3, md: 4 },
        px: 0
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper
          elevation={12}
          sx={{
            background: 'white',
            border: `2px solid ${primaryColor}`,
            borderRadius: { xs: 2, sm: 3, md: 4 },
            boxShadow: {
              xs: `0px 8px 24px rgba(87, 87, 176, 0.2)`,
              sm: `0px 12px 36px rgba(87, 87, 176, 0.25)`,
              md: `0px 16px 48px rgba(87, 87, 176, 0.3), 0px 8px 24px rgba(87, 87, 176, 0.2)`
            },
            position: 'relative',
            overflow: 'hidden',
            maxWidth: { xs: '100%', sm: '95%', md: '90%', lg: '85%' },
            mx: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: { xs: '3px', sm: '4px' },
              background: `linear-gradient(90deg, ${primaryColor} 0%, #7575c7 50%, ${primaryColor} 100%)`,
            }
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header Section with improved responsiveness */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: { xs: 3, sm: 4 }, 
              mt: { xs: 1, sm: 2 }
            }}>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: primaryColor,
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { 
                    xs: '1.75rem', 
                    sm: '2.25rem', 
                    md: '2.75rem',
                    lg: '3rem'
                  },
                  letterSpacing: '0.02em',
                  lineHeight: { xs: 1.2, sm: 1.3 }
                }}
              >
                {t('global_setting.title')}
              </Typography>
              
              {(config.modelTextHost === 'dataunfold' || config.modelVisionHost === 'dataunfold' || 
                config.modelAudioHost === 'dataunfold' || config.modelTextHost === 'dataunfold_mock' || 
                config.modelVisionHost === 'dataunfold_mock' || config.modelAudioHost === 'dataunfold_mock') && (
                <Alert 
                  severity={credit > 0 ? "success" : "warning"}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {credit > 0
                      ? t('global_setting.credit_left', { credit })
                      : t('global_setting.no_credit_left')}
                  </Typography>
                </Alert>
              )}
            </Box>

            {mfaSetupRequired && (
              <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                {t('account_management.mfa_setup_required', 'MFA setup is required. Please complete it in Account Settings to continue.')}
              </Alert>
            )}

            {/* Enhanced Tabs with better mobile support */}
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              mb: { xs: 2, sm: 3 },
              background: {
                xs: 'linear-gradient(135deg, #f8f9fa 0%, #f0f0ff 100%)',
                sm: lightPurple
              },
              borderRadius: { xs: '6px 6px 0 0', sm: '8px 8px 0 0' },
              overflow: 'hidden',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.05)'
            }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Global Settings Tabs"
                sx={{
                  minHeight: { xs: 48, sm: 56, md: 64 },
                  '& .MuiTabs-indicator': {
                    backgroundColor: primaryColor,
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  },
                  '& .MuiTab-iconWrapper': {
                    mr: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    svg: {
                      fontSize: { xs: 18, sm: 20 }
                    }
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: primaryColor,
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                  '& .MuiTabs-flexContainer': {
                    gap: { xs: 0, sm: 1 }
                  },
                  '& .MuiTab-wrapper': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    flexDirection: 'row'
                  },
                  '& .MuiTab-wrapper > span': {
                    display: { xs: 'none', sm: 'inline' }
                  },
                  '& .MuiTab-root': {
                    color: '#666',
                    minHeight: { xs: 56, sm: 56, md: 64 },
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.95rem' },
                    fontWeight: 500,
                    px: { xs: 0.75, sm: 1.5, md: 2 },
                    py: { xs: 1.25, sm: 1.5, md: 2 },
                    minWidth: { xs: 'auto', sm: 120, md: 140 },
                    maxWidth: { xs: 'none', sm: 180, md: 200 },
                    // Enable text wrapping with better control
                    whiteSpace: 'normal',
                    lineHeight: { xs: 1.3, sm: 1.3 },
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    textAlign: 'center',
                    transition: 'all 0.3s ease-in-out',
                    // Allow content to be visible
                    overflow: 'visible',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    borderRadius: '10px',
                    paddingLeft: { xs: 1, sm: 2 },
                    paddingRight: { xs: 1, sm: 2 },
                    '&.Mui-selected': {
                      color: primaryColor,
                      fontWeight: 700,
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      boxShadow: '0 6px 18px rgba(87, 87, 176, 0.08)',
                      border: '1px solid rgba(87, 87, 176, 0.12)',
                      transform: { xs: 'none', sm: 'translateY(-1px)' }
                    },
                    '&:hover': {
                      color: hoverColor,
                      backgroundColor: 'rgba(87, 87, 176, 0.05)',
                      transform: { xs: 'none', sm: 'translateY(-1px)' },
                    }
                  }
                }}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    icon={tab.icon}
                    label={tab.label}
                    iconPosition="start"
                    disabled={mfaSetupRequired && tab.key !== 'account'}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Tab Panels with improved mobile styling */}
            <Box sx={{ 
              borderRadius: { xs: 1.5, sm: 2 },
              minHeight: { xs: 300, sm: 350, md: 400 },
              position: 'relative',
              boxShadow: {
                xs: '0px 2px 8px rgba(0,0,0,0.08)',
                sm: '0px 2px 8px rgba(0,0,0,0.1)'
              }
            }}>
              {tabs.map((tab, index) => (
                <TabPanel key={tab.key} value={tabValue} index={index}>
                  {tab.panel}
                </TabPanel>
              ))}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default GlobalSetting;
